import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

// Courtesy notice for customers arriving at the new ordering portal — both
// legacy artstorefronts users and first-timers. It appears once per browser
// session (sessionStorage), so a returning customer coming back in a fresh
// session is greeted again; it is intentionally NOT permanently retired.
// Behavior is route-aware: on /upload it sits upper-left under the header and
// stays until dismissed; when the customer moves into /visualizer it drops to
// the bottom-left corner and auto-fades after a few seconds.
// The card renders through a portal to document.body so the visualizer's
// canvas/panels can never stack over it or re-anchor its fixed positioning.

const SESSION_KEY = "tpw_welcome_notice_seen";
const VISIBLE_MS = 6000;
const FADE_MS = 500;
const NOTICE_PATHS = ["/upload", "/visualizer"];

// The "seen" flag is written only when the notice actually leaves the screen
// (the customer dismisses it, or the visualizer fade completes) — never on
// mount, so a fresh page load on either route still shows the notice.
function markSeen() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    // storage unavailable (e.g. private mode) — nothing to persist
  }
}

export default function WelcomeNotice() {
  const { pathname } = useLocation();
  const onNoticePath = NOTICE_PATHS.some(p => pathname.startsWith(p));
  const onVisualizer = pathname.startsWith("/visualizer");

  const [show, setShow] = useState(false);
  const [fading, setFading] = useState(false);

  // Show once per browser session. Read-only check — the flag itself is set
  // by markSeen when the notice leaves the screen, never here on mount.
  useEffect(() => {
    try {
      if (!sessionStorage.getItem(SESSION_KEY)) {
        setShow(true);
      }
    } catch {
      // storage unavailable (e.g. private mode) — just show it this once
      setShow(true);
    }
  }, []);

  // Auto-fade ONLY on the visualizer. On /upload the notice stays put until the
  // customer dismisses it. This component lives in AppLayout, which does not
  // unmount between /upload and /visualizer, so the notice rides across that
  // navigation and the fade timer only starts once they reach the visualizer.
  useEffect(() => {
    if (!show || !onVisualizer) {
      setFading(false);
      return;
    }
    const fadeTimer = setTimeout(() => setFading(true), VISIBLE_MS);
    const hideTimer = setTimeout(() => {
      setShow(false);
      markSeen();
    }, VISIBLE_MS + FADE_MS);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [show, onVisualizer]);

  if (!show || !onNoticePath) return null;

  const positionClass = onVisualizer ? "bottom-6 left-6" : "left-6 top-[76px]";

  return createPortal(
    <div
      role="status"
      className={`fixed z-[5000] max-w-[360px] transition-opacity duration-500 ${
        fading ? "opacity-0" : "opacity-100"
      } ${positionClass}`}
    >
      <div className="relative rounded-[10px] border border-[#b7e0e0] bg-[#dbf1f1] px-4 py-3.5 pr-9 text-[13.5px] leading-[1.5] text-[#333] shadow-[0_6px_20px_rgba(0,0,0,0.12)]">
        <strong className="text-[#1a1a1a]">You&rsquo;re in the right place!</strong>{" "}
        Tribeca Printworks has a newly improved ordering experience &mdash; same
        trusted studio, now with more printing, framing, and customization
        options than ever before!
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            setShow(false);
            markSeen();
          }}
          className="absolute right-2.5 top-2 cursor-pointer text-[18px] leading-none text-[#6b7a7a] hover:text-[#333]"
        >
          &times;
        </button>
      </div>
    </div>,
    document.body
  );
}
